import React from "react";
import { useLang } from "@/context/LanguageContext";
import { DataFlowBg } from "@/components/DataFlowBg";
import { Reveal } from "@/components/Reveal";
import {
  PassportMockup,
  IDCardMockup,
  VisaMockup,
  StampMockup,
  LicenceMockup,
  CivilMockup,
} from "@/components/DocumentMockups";
import {
  IdentificationCard,
  Notebook,
  Article,
  ShieldCheck,
  Check,
} from "@phosphor-icons/react";

const DocCard = ({ index, mockup: Mockup, tag, title, delay = 0 }) => (
  <Reveal delay={delay} className="sharp-card overflow-hidden group tilt-3d" data-testid={`doc-card-${index}`}>
    <div className="relative aspect-[16/10] bg-[#131e30] flex items-center justify-center p-6 overflow-hidden border-b border-white/[0.08]">
      <div className="absolute inset-0 bg-circuit opacity-30" />
      <div className="tilt-3d-inner relative w-full max-w-[360px]">
        <Mockup />
      </div>
      <div className="absolute top-3 left-3 font-mono text-[#FF6B35] text-[11px] tracking-wider">
        / {String(index).padStart(2, "0")}
      </div>
    </div>
    <div className="p-5">
      <div className="section-eyebrow text-[10px]">{tag}</div>
      <div className="mt-2 text-white text-base font-heading tracking-tight">{title}</div>
    </div>
  </Reveal>
);

const RangeGroup = ({ icon: Icon, title, items, delay = 0 }) => (
  <Reveal delay={delay} className="sharp-card p-8">
    <div className="flex items-center justify-between">
      <Icon size={26} className="text-[#0089C8]" weight="duotone" />
      <div className="section-eyebrow text-[10px]">{title}</div>
    </div>
    <ul className="mt-6 space-y-2.5">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-slate-200 flex items-start gap-2">
          <span className="mt-1 text-[#0089C8] shrink-0"><Check size={12} weight="bold" /></span>
          {it}
        </li>
      ))}
    </ul>
  </Reveal>
);

const Documents = () => {
  const { t } = useLang();

  const docs = [
    { mock: PassportMockup, key: "p1" },
    { mock: IDCardMockup, key: "p2" },
    { mock: VisaMockup, key: "p3" },
    { mock: StampMockup, key: "p4" },
    { mock: LicenceMockup, key: "p5" },
    { mock: CivilMockup, key: "p6" },
  ];

  const benefits = t("documents.benefits");
  const method = t("documents.method");
  const cards = t("documents.range.cards");
  const passports = t("documents.range.passports");
  const secure = t("documents.range.secure");

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <DataFlowBg density={6} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">{t("documents.eyebrow")}</div>
          <h1 className="h-display mt-6 text-4xl sm:text-5xl lg:text-6xl text-white max-w-4xl">
            {t("documents.title")}
          </h1>
          <p className="mt-8 max-w-3xl text-slate-300 text-base sm:text-lg leading-relaxed">
            {t("documents.intro")}
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="section-eyebrow">/ 01</div>
            <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white">{t("documents.benefitsTitle")}</h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {benefits.map((b, i) => {
                // Column-by-column cascade: fill left column first (even i), then right column (odd i)
                const col = i % 2;
                const row = Math.floor(i / 2);
                const delay = col * 220 + row * 80;
                return (
                  <Reveal
                    as="li"
                    key={i}
                    delay={delay}
                    className="flex items-start gap-3 text-sm text-slate-200 leading-relaxed border-b border-white/[0.08] pb-3"
                  >
                    <span className="mt-1 text-[#0089C8] shrink-0"><Check size={14} weight="bold" /></span>
                    <span>{b}</span>
                  </Reveal>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08] bg-[#131e30]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">/ 02</div>
          <div className="mt-5 grid lg:grid-cols-12 gap-8 items-end">
            <h2 className="h-display text-3xl sm:text-4xl text-white lg:col-span-6">{t("documents.methodTitle")}</h2>
            <p className="text-slate-300 text-base leading-relaxed lg:col-span-6">{t("documents.methodLead")}</p>
          </div>

          <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="methodology-list">
            {method.map((step, i) => (
              <Reveal as="li" key={i} delay={i * 80} className="sharp-card p-6 card-hilite">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[#FF6B35] text-sm tracking-wider">/ 0{i + 1}</span>
                  <span className="text-[10px] uppercase tracking-[0.24em] text-slate-400">Step</span>
                </div>
                <div className="mt-5 text-white font-heading text-lg">{step.t}</div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{step.d}</p>
              </Reveal>
            ))}
          </ol>

          <div className="mt-12 sharp-card p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6">
            <ShieldCheck size={36} className="text-[#0089C8] shrink-0" weight="duotone" />
            <div>
              <div className="text-white font-heading text-lg">{t("documents.supportTitle")}</div>
              <p className="mt-1 text-slate-300 text-sm leading-relaxed">{t("documents.supportDesc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Range */}
      <section className="relative py-20 md:py-24 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">/ 03</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white">{t("documents.rangeTitle")}</h2>

          <div className="mt-12 grid md:grid-cols-3 gap-6" data-testid="range-grid">
            <RangeGroup icon={IdentificationCard} title={cards.title} items={cards.items} delay={0} />
            <RangeGroup icon={Notebook} title={passports.title} items={passports.items} delay={100} />
            <RangeGroup icon={Article} title={secure.title} items={secure.items} delay={200} />
          </div>
        </div>
      </section>

      {/* Visuals */}
      <section className="relative py-16 md:py-24 border-t border-white/[0.08] bg-[#131e30]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">/ 04</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white">{t("documents.productsTitle")}</h2>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {docs.map((d, i) => (
              <DocCard
                key={d.key}
                index={i + 1}
                mockup={d.mock}
                tag={t(`documents.${d.key}.tag`)}
                title={t(`documents.${d.key}.title`)}
                delay={i * 80}
              />
            ))}
          </div>
          <div className="mt-8 text-xs uppercase tracking-[0.22em] text-slate-400">
            {t("documents.specimenNote")}
          </div>
        </div>
      </section>
    </>
  );
};

export default Documents;
